import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTRPCAPI, useTRPCMutation } from '../../../trpc/trpc';

export interface UseFollowTrackOptions {
  onSuccess?: (trackId: string) => void;
  onError?: (error: Error, trackId: string) => void;
}

export const useFollowTrack = (options: UseFollowTrackOptions = {}) => {
  const { onSuccess, onError } = options;
  const trpcMusicAPI = useTRPCAPI();
  const queryClient = useQueryClient();

  // Use refs to always have access to latest callbacks without recreating mutation
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  // Track current trackId for callbacks
  const currentTrackIdRef = useRef<string | null>(null);

  // Follow and unfollow mutations
  const followMutation = useTRPCMutation(trpcMusicAPI.tracks.follow.mutationOptions());
  const unfollowMutation = useTRPCMutation(trpcMusicAPI.tracks.unfollow.mutationOptions());

  const followTrack = useCallback(
    (trackId: string) => {
      currentTrackIdRef.current = trackId;
      followMutation.mutate(
        { trackId },
        {
          onSuccess: () => {
            // Invalidate specific track-related queries to refresh data (async, fire-and-forget)
            Promise.all([
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.tracks.listFollowed.queryKey(),
              }),
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.tracks.isFollowing.queryKey(),
              }),
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.tracks.details.queryKey(),
              }),
            ]).catch(console.error);
            onSuccessRef.current?.(trackId);
          },
          onError: (error) => {
            const errorObj = error instanceof Error ? error : new Error(error.message);
            onErrorRef.current?.(errorObj, trackId);
          },
        }
      );
    },
    [
      followMutation,
      queryClient,
      trpcMusicAPI.tracks.listFollowed,
      trpcMusicAPI.tracks.isFollowing,
      trpcMusicAPI.tracks.details,
    ]
  );

  const unfollowTrack = useCallback(
    (trackId: string) => {
      currentTrackIdRef.current = trackId;
      unfollowMutation.mutate(
        { trackId },
        {
          onSuccess: () => {
            // Invalidate specific track-related queries to refresh data (async, fire-and-forget)
            Promise.all([
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.tracks.listFollowed.queryKey(),
              }),
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.tracks.isFollowing.queryKey(),
              }),
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.tracks.details.queryKey(),
              }),
            ]).catch(console.error);
            onSuccessRef.current?.(trackId);
          },
          onError: (error) => {
            const errorObj = error instanceof Error ? error : new Error(error.message);
            onErrorRef.current?.(errorObj, trackId);
          },
        }
      );
    },
    [
      unfollowMutation,
      queryClient,
      trpcMusicAPI.tracks.listFollowed,
      trpcMusicAPI.tracks.isFollowing,
      trpcMusicAPI.tracks.details,
    ]
  );

  return {
    followTrack,
    unfollowTrack,
    isFollowPending: followMutation.isPending,
    isUnfollowPending: unfollowMutation.isPending,
    isPending: followMutation.isPending || unfollowMutation.isPending,
  };
};
